import * as records from "data/records";
import { experienceApi } from "data/resources/experiences";
import { crud } from "./utils/crud";
import { request } from "./utils/request";
import { search } from "./utils/search";

/*
design spitballing:

Stuff I'd like to be able to do:
apiv3.trips.search({user: userId, keywords: 'mtb', distance: [0, 20000]})
  .then(({results, resultsCount}) => {
    // results: PER_PAGE sized array of deserialized SearchResult Record() objects
    // resultsCount: total results returned by DB
  })

apiv3.trips.index({order: 'departed_at DESC'})
  .then({results, resultsCount} => ...)

// open question - how to handle pagination?

apiv3.trips.create({name, filehandle, visibility})
  .then(deserializedRide => ...)

apiv3.trips.read(rideId)
  .then(deserializedRide => ...)

*/

// maybe let's do it ghetto-style and then make it good?

const experienceRoutes = crud(records.ExperienceRoute);
experienceRoutes.reorder = (experienceId, orderedIds) =>
  request
    .post(`/experience_routes/reorder`, {
      params: { experience_id: experienceId },
      body: JSON.stringify({
        ordered: orderedIds.toJS().reduce((map, id, position) => {
          map[id] = position;
          return map;
        }, {}),
      }),
    })
    .then(({ results, ...rest }) => ({
      results: results.map(records.ExperienceRoute.deserialize),
      ...rest,
    })); // copied over from crud, crud

const trips = crud(records.Trip);
const routes = crud(records.Route);

const usersExtras = {
  feedItems: (id, params) =>
    request.get(`/users/${id}/dashboard_feed_items`, { params }).then(({ results, extras, ...rest }) => {
      const deserializedExtras = extras.map((e) => {
        if (e.type === "route") {
          return records.Route.deserialize(e.route);
        } else if (e.type === "trip") {
          return records.Trip.deserialize(e.trip);
        } else if (e.type === "gear") {
          return records.Gear.deserialize(e.gear);
        } else if (e.type === "subscription") {
          return records.Subscription.deserialize(e.subscription);
        }
      });
      return {
        results: results.map(records.FeedItem.deserialize),
        extras: deserializedExtras,
        ...rest,
      };
    }),
  siteActivity: (id, params) =>
    request.get(`/user_activities`, { params }).then(({ results, ...rest }) => ({
      results: results.map(records.SiteActivity.deserialize),
      ...rest,
    })),
  recordActivity: (action, objectType, objectId) =>
    request.post(`/user_activities/record`, {
      params: {
        ua_action: action,
        object_type: objectType,
        object_id: objectId,
      },
    }),
  friends: (id, params) => request.get(`/users/${id}/friends.json`, { params }),
  unfriend: (id) => request.post(`/users/${id}/unfriend.json`),
  befriend: (id) => request.post(`/users/${id}/befriend.json`),
  events: (id, params) => request.get(`/users/${id}/events.json`, { params }),
  friendRequests: (id) => request.get(`/users/${id}/friend_requests.json`),
  confirmFriendRequest: (id) => request.post(`/users/${id}/confirm_friend_request.json`),
  declineFriendRequest: (id) => request.post(`/users/${id}/decline_friend_request.json`),
  pinned: (id, params) =>
    // if you're after particular favorited `object_type`s, you have to pass
    // {object_type: JSON.stringify(['Segment', 'Trip', 'etc.']) in your params.
    // by default, the backend will return %w(Route Trip User)
    request.get(`/users/${id}/pinned`, { params }),
  createdSegments: (id, params) => request.get(`/users/${id}/created_segments`, { params }),
  gears: (id) => request.get(`/users/${id}/gears.json?apikey=x&version=3`),
};

const users = { ...crud(records.User), ...usersExtras };

const relationships = {
  confirm: (id) =>
    request
      .post(`/relationships/${id}/confirm.json?apikey=x&version=3`, {
        body: JSON.stringify({ relationship_type: "acquaintance" }),
      })
      .then(({ relationship, ...rest }) => {
        return {
          relationship: records.Relationship.deserialize(relationship),
          ...rest,
        };
      }),
  decline: (id) =>
    request.post(`/relationships/${id}/decline.json?apikey=x&version=3`).then(({ relationship, ...rest }) => {
      return {
        relationship: records.Relationship.deserialize(relationship),
        ...rest,
      };
    }),
  delete: (id) => request.delete(`/relationships/${id}`),
};

const events = crud(records.Event);

export const apiv3 = {
  experiences: experienceApi,
  experienceRoutes,
  trips,
  routes,
  users,
  relationships,
  search,
  events,
};
